"use client";

import Link from "next/link";
import CaretRightIcon from "public/next-public/common/icons/caret-solid-right.svg";
import CartIcon from "public/next-public/common/icons/cart-16.svg";
import React from "react";
import primaryStyles from "./buttonPrimary.module.scss";
import secondaryStyles from "./buttonSecondary.module.scss";
import tertiaryStyles from "./buttonTertiary.module.scss";

const styleMap = {
  primary: primaryStyles,
  secondary: secondaryStyles,
  tertiary: tertiaryStyles,
} as const;

interface BaseButtonProps {
  text: string;
  size?: "large" | "small";
  color?: "gray" | "teal" | "blue" | "white" | "destructive";
  showLeftIcon?: boolean;
  showRightIcon?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  iconOnly?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  /** Id of the element describing the button — e.g. the message saying why it is unavailable. */
  ariaDescribedBy?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  /**
   * When provided, the button renders as a Next.js `<Link>` (which emits an `<a>`)
   * for navigation instead of a `<button>`. `rel="noopener noreferrer"` is injected
   * automatically when `target === "_blank"` unless an explicit `rel` is given.
   */
  link?: {
    href: string;
    target?: string;
    rel?: string;
  };
  /** `data-*` attributes land on the rendered element, e.g. stable hooks for session-replay tools. */
  [dataAttribute: `data-${string}`]: string | number | boolean | undefined;
}

interface PrimarySecondaryButtonProps extends BaseButtonProps {
  variant: "primary" | "secondary";
  loading?: boolean;
  rounded?: boolean;
}

interface TertiaryButtonProps extends BaseButtonProps {
  variant: "tertiary";
  loading?: never;
  rounded?: never;
}

type ButtonProps = PrimarySecondaryButtonProps | TertiaryButtonProps;

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant,
      text,
      size = "large",
      color = "gray",
      showLeftIcon = true,
      showRightIcon = true,
      leftIcon,
      rightIcon,
      iconOnly = false,
      ariaLabel,
      ariaExpanded,
      ariaControls,
      ariaDescribedBy,
      rounded = false,
      loading = false,
      disabled = false,
      className,
      id,
      onClick,
      type = "button",
      link,
      ...dataAttributes
    },
    ref,
  ) => {
    const styles = styleMap[variant];
    const isTertiary = variant === "tertiary";
    const isDisabled = disabled || (!isTertiary && loading);

    const classNames = [
      styles.Button,
      styles[size],
      styles[color],
      iconOnly && styles.iconOnly ? styles.iconOnly : "",
      !isTertiary && rounded ? styles.rounded : "",
      !isTertiary && loading ? styles.loading : "",
      isDisabled ? styles.disabled : "",
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    // An explicit ariaLabel wins, so callers can disambiguate buttons that repeat a label.
    const accessibleName = iconOnly ? ariaLabel || text : ariaLabel;

    const inner = (
      <>
        {(iconOnly || showLeftIcon) && (
          <span className={styles.icon}>{leftIcon || <CartIcon width={16} height={16} />}</span>
        )}
        {!iconOnly && <span className={styles.label}>{text}</span>}
        {!iconOnly && showRightIcon && (
          <span className={styles.icon}>{rightIcon || <CaretRightIcon width={16} height={16} />}</span>
        )}
        {!isTertiary && loading && <span className={styles.loader} />}
      </>
    );

    if (link) {
      if (isDisabled) {
        return (
          <a
            {...dataAttributes}
            ref={ref as React.Ref<HTMLAnchorElement>}
            className={classNames}
            id={id}
            aria-label={accessibleName}
            aria-describedby={ariaDescribedBy}
            aria-disabled
          >
            {inner}
          </a>
        );
      }
      // Default to a safe rel for new-tab links to prevent tabnabbing.
      const safeRel = link.target === "_blank" ? (link.rel ?? "noopener noreferrer") : link.rel;
      return (
        <Link
          {...dataAttributes}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classNames}
          id={id}
          href={link.href}
          target={link.target}
          rel={safeRel}
          aria-label={accessibleName}
          aria-describedby={ariaDescribedBy}
          onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        >
          {inner}
        </Link>
      );
    }

    return (
      <button
        {...dataAttributes}
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classNames}
        id={id}
        onClick={onClick}
        disabled={isDisabled}
        type={type}
        aria-label={accessibleName}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-describedby={ariaDescribedBy}
      >
        {inner}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
